import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/common/Widgets";
import { Home, FileText, Scale, Search, Loader2, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { fmtNum, fmtDate } from "@/lib/format";

export default function PropertySection({ app, reload }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({
    property_type: "RESIDENTIAL", property_address: app?.property_location || "",
    property_state: "Maharashtra", property_pincode: "", survey_no: "", builder_name: app?.builder_name || "",
    rera_number: app?.rera_number || "", declared_value_lakhs: app?.property_cost_lakhs || "",
    valuer_agency: "", legal_firm: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/property", { params: { application_id: app.id } });
      setOrders(r.data || []);
    } catch (e) { /* nothing yet */ }
    finally { setLoading(false); }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  const order = async (kind) => {
    if (!form.property_address) return toast.error("Property address is required");
    setBusy(kind);
    try {
      const payload = {
        application_id: app.id,
        property_type: form.property_type,
        property_address: form.property_address,
        property_state: form.property_state,
        property_pincode: form.property_pincode,
        survey_no: form.survey_no,
        builder_name: form.builder_name,
        rera_number: form.rera_number,
        declared_value_lakhs: form.declared_value_lakhs ? Number(form.declared_value_lakhs) : null,
        valuer_agency: form.valuer_agency,
        legal_firm: form.legal_firm,
      };
      if (kind === "technical") await api.post("/property/technical/initiate", payload);
      else if (kind === "legal") await api.post("/property/legal/initiate", payload);
      else if (kind === "cersai") await api.post("/property/cersai/check", payload);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} order placed (mocked)`);
      load(); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); }
    finally { setBusy(null); }
  };

  const tech = orders.find(o => o.kind === "TECHNICAL_VALUATION");
  const legal = orders.find(o => o.kind === "LEGAL_VERIFICATION");
  const cersai = orders.find(o => o.kind === "CERSAI_SEARCH");

  if (loading) return <Card className="p-4 space-y-2">{[0,1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>;

  return (
    <div className="space-y-4" data-testid="property-section">
      <SectionHeader title="Property Valuation" subtitle="Technical valuation · Legal opinion · CERSAI charge search. All mandatory for Housing / Mortgage loans." />
      <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <FieldS label="Property Type"><Select value={form.property_type} onValueChange={v => set("property_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="RESIDENTIAL">Residential</SelectItem><SelectItem value="COMMERCIAL">Commercial</SelectItem><SelectItem value="LAND">Land</SelectItem><SelectItem value="INDUSTRIAL">Industrial</SelectItem></SelectContent></Select></FieldS>
        <FieldS label="State"><Input value={form.property_state} onChange={e => set("property_state", e.target.value)} /></FieldS>
        <FieldS label="Pincode"><Input value={form.property_pincode} onChange={e => set("property_pincode", e.target.value)} /></FieldS>
        <div className="md:col-span-3"><FieldS label="Property Address *"><Input value={form.property_address} onChange={e => set("property_address", e.target.value)} data-testid="property-address" /></FieldS></div>
        <FieldS label="Survey No."><Input value={form.survey_no} onChange={e => set("survey_no", e.target.value)} /></FieldS>
        <FieldS label="Builder Name"><Input value={form.builder_name} onChange={e => set("builder_name", e.target.value)} /></FieldS>
        <FieldS label="RERA No."><Input value={form.rera_number} onChange={e => set("rera_number", e.target.value)} /></FieldS>
        <FieldS label="Declared Value (₹ L)"><Input type="number" value={form.declared_value_lakhs} onChange={e => set("declared_value_lakhs", e.target.value)} /></FieldS>
        <FieldS label="Valuer Agency"><Input value={form.valuer_agency} onChange={e => set("valuer_agency", e.target.value)} placeholder="e.g. Knight Frank" /></FieldS>
        <FieldS label="Legal Firm"><Input value={form.legal_firm} onChange={e => set("legal_firm", e.target.value)} placeholder="e.g. AZB &amp; Partners" /></FieldS>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <OrderCard
          title="Technical Valuation"
          icon={Home} color="primary"
          order={tech}
          actionLabel={tech ? "Re-Order" : "Order Now"}
          busy={busy === "technical"}
          onClick={() => order("technical")}
          dataTestId="order-technical"
        >{tech && (
          <div className="text-xs space-y-1 mt-2">
            <Row label="Market Value" value={`₹ ${fmtNum(tech.market_value_lakhs, 2)} L`} />
            <Row label="Forced Sale" value={`₹ ${fmtNum(tech.forced_sale_value_lakhs, 2)} L`} />
            <Row label="Valuer" value={tech.valuer_agency} />
            <Row label="License" value={tech.valuer_license} mono />
            <Row label="Date" value={fmtDate(tech.valuation_date)} />
          </div>
        )}</OrderCard>
        <OrderCard
          title="Legal Opinion"
          icon={Scale} color="accent"
          order={legal}
          actionLabel={legal ? "Re-Order" : "Order Now"}
          busy={busy === "legal"}
          onClick={() => order("legal")}
          dataTestId="order-legal"
        >{legal && (
          <div className="text-xs space-y-1 mt-2">
            <Row label="Opinion" value={<Badge variant="outline" className={`text-[10px] ${legal.encumbrance_clear ? "text-success border-success/40" : "text-warning border-warning/40"}`}>{legal.opinion}</Badge>} />
            <Row label="Title chain" value={`${legal.title_chain_years} yrs`} />
            <Row label="Advocate" value={legal.advocate_name} />
            <Row label="Firm" value={legal.legal_firm} />
            <Row label="Date" value={fmtDate(legal.opinion_date)} />
            {legal.opinion_notes && <div className="text-muted-foreground italic mt-1 leading-snug">{legal.opinion_notes}</div>}
          </div>
        )}</OrderCard>
        <OrderCard
          title="CERSAI Charge Search"
          icon={Search} color="info"
          order={cersai}
          actionLabel={cersai ? "Re-Search" : "Search Now"}
          busy={busy === "cersai"}
          onClick={() => order("cersai")}
          dataTestId="order-cersai"
        >{cersai && (
          <div className="text-xs space-y-1 mt-2">
            <Row label="Status" value={<Badge variant="outline" className={`text-[10px] ${cersai.is_clear ? "text-success border-success/40" : "text-destructive border-destructive/40"}`}>{cersai.is_clear ? "CLEAR" : "EXISTING CHARGES"}</Badge>} />
            <Row label="Searched" value={fmtDate(cersai.searched_at)} />
            {cersai.charges_found?.length > 0 && (
              <div className="mt-1 text-[11px] text-destructive">{cersai.charges_found.length} existing charge(s) by {cersai.charges_found[0].holder_bank}</div>
            )}
          </div>
        )}</OrderCard>
      </div>
      {orders.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border"><h3 className="text-sm font-semibold heading">Order History</h3></div>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Kind</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Outcome</th><th></th></tr></thead>
            <tbody>{orders.map(o => (<tr key={o.id} className="border-t border-border">
              <td className="px-3 py-1.5 mono text-xs text-muted-foreground">{fmtDate(o.created_at)}</td>
              <td className="px-3 py-1.5 text-xs"><Badge variant="outline" className="text-[10px]">{o.kind}</Badge></td>
              <td className="px-3 py-1.5 text-xs"><Badge variant="outline" className="text-[10px] text-success border-success/40">{o.status}</Badge></td>
              <td className="px-3 py-1.5 text-xs">{o.opinion || (o.is_clear !== undefined ? (o.is_clear ? "CLEAR" : "CHARGES") : (o.market_value_lakhs ? `₹${o.market_value_lakhs}L` : "—"))}</td>
              <td className="px-3 py-1.5">{o.report_url && <a href={o.report_url} target="_blank" rel="noopener noreferrer" className="text-accent text-xs underline inline-flex items-center gap-1">Report<ExternalLink className="h-3 w-3" /></a>}</td>
            </tr>))}</tbody>
          </table></div>
        </Card>
      )}
    </div>
  );
}

function OrderCard({ title, icon: Icon, color, order, actionLabel, busy, onClick, dataTestId, children }) {
  const isDone = !!order;
  const colorMap = { primary: "border-primary/30 bg-primary/5 text-primary", accent: "border-accent/30 bg-accent/5 text-accent", info: "border-info/30 bg-info/5 text-info" };
  return (
    <Card className="p-4 flex flex-col gap-2" data-testid={dataTestId}>
      <div className={`inline-flex items-center gap-2 ${colorMap[color] ? "" : ""}`}>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center border ${colorMap[color] || ""}`}><Icon className="h-4 w-4" /></div>
        <div><h3 className="text-sm font-semibold heading">{title}</h3>
          {isDone ? <Badge variant="outline" className="text-[10px] text-success border-success/40 mt-0.5"><CheckCircle2 className="h-3 w-3 mr-0.5" />Completed</Badge> : <Badge variant="outline" className="text-[10px] text-muted-foreground mt-0.5">Not ordered</Badge>}
        </div>
      </div>
      <div className="flex-1">{children}</div>
      <Button variant={isDone ? "outline" : "default"} size="sm" onClick={onClick} disabled={busy} className="mt-2">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}{actionLabel}</Button>
    </Card>
  );
}

function FieldS({ label, children }) {
  return (<div className="space-y-1"><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}
function Row({ label, value, mono }) {
  return (<div className="flex justify-between gap-2"><span className="text-muted-foreground">{label}</span><span className={`font-medium ${mono ? "mono" : ""}`}>{value}</span></div>);
}
